import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from './auth/auth.config';
import { UserProfile } from './auth/user-profile';
import { NgClass } from '@angular/common';
import { KeycloakAdminApi } from './services/keycloak-admin-api';
import { environment } from './environments/environment';
import { SidebarService } from './services/sidebar.service';
import { UserEdit } from './components/user-edit/user-edit';
import { GroupEdit } from './components/group-edit/group-edit';
import { Title } from '@angular/platform-browser';

const RETRY_STORAGE_KEY = 'oauth_login_retries';
const MAX_LOGIN_RETRIES = 2;
const RETRY_WINDOW_MS = 60_000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, NgClass, UserEdit, GroupEdit],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class App implements OnInit, OnDestroy {
  private oauthEventsSubscription: Subscription | undefined;
  private oauthService = inject(OAuthService);
  private keycloakApi = inject(KeycloakAdminApi);
  private router = inject(Router);

  // Expose sidebar service to template
  sidebarService = inject(SidebarService);
  titleService = inject(Title);

  isAuthenticated = signal(false);
  userProfile = signal<UserProfile | undefined>(undefined);
  isSideBarOpen = signal(true);
  canManageRealms = signal(false);
  canQueryClients = signal(false);
  canQueryUsers = signal(false);
  canQueryGroups = signal(false);
  isRealmAdmin = signal(false);
  authError = signal<'session-error' | 'unavailable' | null>(null);
  environmentName: 'dev' | 'staging' | 'production' | string = environment.name;

  ngOnInit(): void {
    this.setAppTitle();
    this.configureOAuth();
  }

  private setAppTitle(): void {
    const baseTitle = 'Customer Identity Admin';
    let envAddition;
    switch (environment.name) {
      case 'dev':
        envAddition = ` [DEV]`;
        break;
      case 'staging':
        envAddition = ` [STAGING]`;
        break;
      case 'production':
        envAddition = ``;
        break;
      default:
        envAddition = ` [${environment.name.toUpperCase()}]`;
        break;
    }
    this.titleService.setTitle(`${baseTitle}${envAddition}`);
  }

  private configureOAuth(): void {
    this.oauthService.configure(authConfig);

    // Subscribe to events BEFORE loading the discovery document so no event is missed.
    // token_received fires when the code exchange succeeds — more reliable than the
    // loadDiscoveryDocumentAndTryLogin() promise, which can resolve before the token
    // is fully stored (race condition that manifests as a blank screen after redirect).
    this.oauthEventsSubscription = this.oauthService.events
      .pipe(filter((e) => e.type === 'token_received'))
      .subscribe(() => this.onAuthSuccess());

    // Catch token / code exchange errors emitted as events (e.g. state mismatch,
    // invalid nonce, token validation failure) and attempt automatic recovery.
    this.oauthService.events
      .pipe(
        filter(
          (e) =>
            e.type === 'token_error' ||
            e.type === 'token_validation_error' ||
            e.type === 'code_error' ||
            e.type === 'invalid_nonce_in_state'
        )
      )
      .subscribe((e) => {
        if (!this.isAuthenticated()) {
          console.error('OAuth event error, attempting recovery:', e);
          this.tryRecoverWithRetry('session-error');
        }
      });

    // Catch discovery document errors (Keycloak unreachable).
    this.oauthService.events
      .pipe(filter((e) => e.type === 'discovery_document_load_error'))
      .subscribe((e) => {
        if (!this.isAuthenticated()) {
          console.error('OAuth discovery document error:', e);
          this.tryRecoverWithRetry('unavailable');
        }
      });

    this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(async () => {
        if (this.oauthService.hasValidAccessToken()) {
          await this.onAuthSuccess();
        } else if (!this.isCallbackUrl()) {
          let stateString = this.router.url;
          stateString = stateString.startsWith('/') ? stateString.substring(1) : stateString;
          this.oauthService.initCodeFlow(stateString);
        }
      })
      .catch((error) => {
        console.error('Error loading discovery document and login', error);
        // OAuthErrorEvent objects carry the error kind in `.type`, not `.message`.
        // Fall back to stringifying the message for plain Error objects.
        const errorType = String(error?.type ?? '');
        const errorMsg = String(error?.message ?? error ?? '');
        const SESSION_ERROR_TYPES = [
          'invalid_nonce_in_state',
          'token_error',
          'token_validation_error',
          'code_error',
        ];
        // State / nonce / token errors are recoverable by starting a fresh login.
        // Network or unknown errors suggest Keycloak may be unavailable.
        const isSessionError =
          SESSION_ERROR_TYPES.includes(errorType) ||
          /state|nonce|token|invalid/i.test(errorType) ||
          /state|nonce|token|invalid/i.test(errorMsg);
        this.tryRecoverWithRetry(isSessionError ? 'session-error' : 'unavailable');
      });
  }

  /** Returns true when the current URL carries an IDP authorization callback. */
  private isCallbackUrl(): boolean {
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return params.has('code') || hash.has('code') || params.has('session_state');
  }

  /**
   * Attempts to recover from an OAuth error by clearing local OAuth state and
   * re-initiating the code flow. Falls back to showing an error screen after
   * MAX_LOGIN_RETRIES attempts within RETRY_WINDOW_MS to prevent infinite loops.
   */
  private tryRecoverWithRetry(errorKind: 'session-error' | 'unavailable'): void {
    const stored = sessionStorage.getItem(RETRY_STORAGE_KEY);
    const data: { count: number; timestamp: number } = stored
      ? JSON.parse(stored)
      : { count: 0, timestamp: Date.now() };

    // Reset counter when outside the retry time window.
    if (Date.now() - data.timestamp > RETRY_WINDOW_MS) {
      data.count = 0;
      data.timestamp = Date.now();
    }

    if (data.count < MAX_LOGIN_RETRIES && errorKind === 'session-error') {
      data.count++;
      sessionStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify(data));
      console.warn(`Attempting OAuth recovery (attempt ${data.count}/${MAX_LOGIN_RETRIES})...`);
      // Clear stored tokens/state without redirecting to IdP logout endpoint.
      this.oauthService.logOut(true);
      this.oauthService.initCodeFlow();
    } else {
      // Either Keycloak is unavailable, or retries are exhausted — show an error screen.
      sessionStorage.removeItem(RETRY_STORAGE_KEY);
      this.authError.set(errorKind);
    }
  }

  private async onAuthSuccess(): Promise<void> {
    // Make sure it is only called once per successful login by checking if we are already authenticated.
    if (this.isAuthenticated()) {
      return;
    }
    this.oauthService.setupAutomaticSilentRefresh();
    this.userProfile.set(this.oauthService.getIdentityClaims() as UserProfile);
    this.isAuthenticated.set(true);
    // isRealmAdmin is authoritative from the backend, not derived from JWT roles here
    const detailPermissions = await this.keycloakApi.getMe();
    this.isRealmAdmin.set(detailPermissions.isRealmAdmin);
    if (detailPermissions.isRealmAdmin) {
      this.canManageRealms.set(true);
      this.canQueryClients.set(true);
      this.canQueryUsers.set(true);
      this.canQueryGroups.set(true);
    } else {
      this.canQueryClients.set(detailPermissions.accessibleClients.length > 0);
      const hasAtLeastOneIaUsersPerm = !!detailPermissions.accessibleClients.find(
        (ac) => !!ac.permissions?.find((perm) => 'ia_users' === perm)
      );
      this.canQueryUsers.set(hasAtLeastOneIaUsersPerm);
      this.canQueryGroups.set(hasAtLeastOneIaUsersPerm);
    }
    const url = this.oauthService.state;
    if (url) {
      await this.router.navigateByUrl(url);
    }
    // Successful login — clear any lingering retry counter.
    sessionStorage.removeItem(RETRY_STORAGE_KEY);
  }

  ngOnDestroy(): void {
    this.oauthEventsSubscription?.unsubscribe();
  }

  logoutUser() {
    this.oauthService.logOut();
  }

  retryLogin() {
    sessionStorage.removeItem(RETRY_STORAGE_KEY);
    window.location.href = window.location.origin;
  }

  toggleSidebar() {
    this.isSideBarOpen.set(!this.isSideBarOpen());
  }
}
